import { useState, useCallback } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";
import { graphqlRequest } from "../../Graphql/graphqlClient";
import { DRIVER_GET_DRIVER_LIST } from "../../Graphql/queries/driverQueries";

export default function useDriver() {
  const {
    api,
    bearerApi,
    AdminId,
    apiGraphqlUrl,
    GraphToken,
    handleApiResponseCode,
  } = useGlobalContext();

  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [settlementRows, setSettlementRows] = useState([]);
  const [settlementType, setSettlementType] = useState("Settlement Amount");
  const [settlementLoading, setSettlementLoading] = useState(false);

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "01" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  const handleGraphqlApiResponse = (responseData) => {
    if (!responseData) return false;

    if (
      responseData?.responseCode === "02" ||
      responseData?.responseCode === "03" ||
      responseData?.responseCode === "98" ||
      responseData?.responseCode === "401"
    ) {
      handleApiResponseCode?.(responseData);
      return true;
    }

    return false;
  };

  const fetchDrivers = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);

      try {
        if (!apiGraphqlUrl || !GraphToken) {
          toast.error("GraphQL configuration missing");
          setDrivers([]);
          return [];
        }

        const data = await graphqlRequest({
          url: apiGraphqlUrl,
          query: DRIVER_GET_DRIVER_LIST,
          variables: {
            adminId: AdminId || "",
            userid: "",
          },
          token: GraphToken,
          authType: "Basic",
        });

        const driverResponse = data?.driverGetDriverList;

        if (handleGraphqlApiResponse(driverResponse)) {
          setDrivers([]);
          return [];
        }

        if (driverResponse?.responseCode === "00") {
          const list = Array.isArray(driverResponse?.data)
            ? driverResponse.data
            : [];

          setDrivers(list);
          return list;
        }

        if (shouldShowLocalError(driverResponse)) {
          toast.error(
            driverResponse?.responseMessage || "Failed to fetch drivers"
          );
        }

        setDrivers([]);
        return [];
      } catch (err) {
        console.error("fetchDrivers error:", err);
        toast.error(err?.message || "Error fetching drivers");
        setDrivers([]);
        return [];
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [apiGraphqlUrl, GraphToken, AdminId, handleApiResponseCode]
  );

  // ================= SETTLEMENT AMOUNT =================
  // Original API used Basic token, so use api
  const fetchSettlementAmount = useCallback(
    async ({ fromDate, toDate, silent = false } = {}) => {
      if (!fromDate || !toDate) {
        toast.error("Please select from date and to date");
        return [];
      }

      if (!silent) setSettlementLoading(true);

      try {
        const res = await api.post("/api/Admin/v2.0/getSettlementAmount", {
          fromDate,
          toDate,
        });

        if (res.data?.responseCode === "00") {
          const response = res.data?.response || {};
          const rows = Array.isArray(response?.request)
            ? response.request
            : [];

          setSettlementType(response?.type || "Settlement Amount");
          setSettlementRows(rows);

          return rows;
        }

        if (shouldShowLocalError(res.data)) {
          toast.error(
            res.data?.responseMessage || "Failed to fetch settlement amount"
          );
        }

        setSettlementRows([]);
        return [];
      } catch (err) {
        console.error("fetchSettlementAmount error:", err);
        toast.error("Error fetching settlement amount");
        setSettlementRows([]);
        return [];
      } finally {
        if (!silent) setSettlementLoading(false);
      }
    },
    [api]
  );

  // ================= DELETE DRIVER =================
  // Original API used Bearer token, so use bearerApi
  const deleteDriver = async (driverId) => {
    if (!driverId) {
      toast.error("Invalid driver ID");
      return false;
    }

    try {
      const res = await bearerApi.post("/api/Driver/v1.0/deleteDriver", {
        driverId,
        adminId: AdminId,
      });

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Driver deleted");

        setDrivers((prev) =>
          (prev || []).filter((driver) => driver.driverId !== driverId)
        );

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to delete driver");
      }

      return false;
    } catch (error) {
      console.error("deleteDriver error:", error);
      toast.error("Error deleting driver");
      return false;
    }
  };

  // ================= TOGGLE DUTY STATUS =================
  // Original API used Basic token, so use api
  const toggleDutyStatus = async (driver, isOnDuty) => {
    if (!driver?.driverId) {
      toast.error("Invalid driver");
      return false;
    }

    const nextStatus = isOnDuty ? "off_duty" : "on_duty";
    const previousStatus = driver?.dutyStatus;

    setDrivers((prev) =>
      (prev || []).map((item) =>
        item.driverId === driver.driverId
          ? { ...item, dutyStatus: nextStatus }
          : item
      )
    );

    try {
      const res = await api.post("/api/User/v1.0/updateDriverDutyTime", {
        device: "Admin",
        userId: "",
        driverId: driver.driverId,
        dutyValue: nextStatus,
        adminId: AdminId,
      });

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Duty status updated");
        await fetchDrivers(true);
        return true;
      }

      setDrivers((prev) =>
        (prev || []).map((item) =>
          item.driverId === driver.driverId
            ? { ...item, dutyStatus: previousStatus }
            : item
        )
      );

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage || "Failed to update duty status"
        );
      }

      return false;
    } catch (error) {
      console.error("toggleDutyStatus error:", error);

      setDrivers((prev) =>
        (prev || []).map((item) =>
          item.driverId === driver.driverId
            ? { ...item, dutyStatus: previousStatus }
            : item
        )
      );

      toast.error("Error updating duty status");
      return false;
    }
  };

  // ================= CREATE DRIVER =================
  // Original API used Basic token, so use api
  const createDriverApi = async (payload, onSuccess) => {
    if (!payload) {
      toast.error("Driver payload is required");
      return false;
    }

    try {
      const res = await api.post("/api/Driver/v1.0/createDriver", payload);

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Driver added");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        await fetchDrivers(true);
        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to add driver");
      }

      return false;
    } catch (error) {
      console.error("createDriverApi error:", error);
      toast.error("Error adding driver");
      return false;
    }
  };

  // ================= EDIT DRIVER =================
  // Original API used Bearer token, so use bearerApi
  const editDriverApi = async (payload) => {
    if (!payload) {
      toast.error("Driver payload is required");
      return false;
    }

    try {
      const res = await bearerApi.post("/api/Driver/v1.0/editDriver", payload);

      if (res.data?.responseCode === "00") {
        toast.success(
          res.data?.responseMessage || "Driver updated successfully"
        );

        await fetchDrivers(true);
        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to update driver");
      }

      return false;
    } catch (err) {
      console.error("editDriverApi error:", err);
      toast.error("Error occurred while updating driver");
      return false;
    }
  };

  // ================= ASSIGN TASK TO DRIVER =================
  // Original API used Basic token, so use api
  const AssignTaskToDriver = async (taskId, driverId) => {
    if (!taskId || !driverId) {
      toast.error("Task or Driver not selected");
      return false;
    }

    try {
      const res = await api.post("/api/Task/v1.0/assignDriver", {
        taskId,
        driverId,
        adminId: AdminId,
      });

      if (res.data?.responseCode === "00") {
        toast.success(
          res.data?.responseMessage || "Driver assigned successfully"
        );
        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to assign driver");
      }

      return false;
    } catch (err) {
      console.error("AssignTaskToDriver error:", err);
      toast.error("Error assigning driver");
      return false;
    }
  };

  // ================= ACTIVATE / DEACTIVATE DRIVER =================
  // Original API used Basic token, so use api
  const deactivateActivateDriverApi = async (driver) => {
    if (!driver?.driverId) {
      toast.error("Invalid driver");
      return false;
    }

    const currentStatus = String(driver?.status || "").toLowerCase();
    const nextStatus = currentStatus === "active" ? "deactivate" : "active";

    try {
      const res = await api.post("/api/Admin/v1.0/deactivateDriver", {
        adminId: String(AdminId),
        driverId: driver.driverId,
        status: nextStatus,
      });

      if (res.data?.responseCode === "00") {
        toast.success(
          res.data?.responseMessage ||
            `Driver ${
              nextStatus === "active" ? "activated" : "deactivated"
            } successfully`
        );

        await fetchDrivers(true);
        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Status update failed");
      }

      return false;
    } catch (err) {
      console.error("deactivateActivateDriverApi error:", err);
      toast.error("Error updating driver status");
      return false;
    }
  };

  // ================= UPDATE DRIVER DUTY =================
  // Original API used Basic token, so use api
  const updateDriverDutyApi = async (payload) => {
    if (!payload) {
      toast.error("Duty payload is required");
      return false;
    }

    try {
      const res = await api.post(
        "/api/Driver/v1.0/updateDriverDutyType",
        payload
      );

      if (res.data?.responseCode === "00") {
        toast.success(
          res.data?.responseMessage || "Driver duty time updated successfully!"
        );

        await fetchDrivers(true);
        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage || "Failed to update driver duty time"
        );
      }

      return false;
    } catch (error) {
      console.error("updateDriverDutyApi error:", error);
      toast.error("Error occur while updating driver duty time");
      return false;
    }
  };

  return {
    drivers,
    loading,
    fetchDrivers,

    settlementRows,
    settlementType,
    settlementLoading,
    fetchSettlementAmount,

    editDriverApi,
    deleteDriver,
    toggleDutyStatus,
    createDriverApi,
    AssignTaskToDriver,
    deactivateActivateDriverApi,
    updateDriverDutyApi,
  };
}